import { PieChart, Trophy } from "lucide-react";
import { Card } from "@components/ui/card";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export function GamesPrizes({
  allPlayerStats,
  loading,
  error,
  startDate,
  endDate,
}: {
  allPlayerStats: any;
  loading: boolean;
  error: string | null;
  startDate?: Date;
  endDate?: Date;
}) {
  // Defensive: support both array and object with players array
  let dataArray = Array.isArray(allPlayerStats)
    ? allPlayerStats
    : allPlayerStats &&
      typeof allPlayerStats === "object" &&
      "players" in allPlayerStats &&
      Array.isArray((allPlayerStats as any).players)
    ? (allPlayerStats as any).players
    : [];

  // Filter by date range if provided
  if (startDate || endDate) {
    dataArray = dataArray.filter((p: any) => {
      if (!p.signupDate) return false;
      const d = new Date(p.signupDate);
      if (isNaN(d.getTime())) return false;
      if (startDate && d < startDate) return false;
      if (endDate && d > endDate) return false;
      return true;
    });
  }

  // Group by month for now (can be enhanced later)
  const grouped = {} as Record<string, { players: number; prizes: number }>;
  dataArray.forEach((p: any) => {
    const date = new Date(p.signupDate);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}`;
    if (!grouped[key]) grouped[key] = { players: 0, prizes: 0 };
    grouped[key].players += 1;
    grouped[key].prizes += Number(p.prizesWon || 0);
  });
  const labels = Object.keys(grouped).sort();
  const playersData = labels.map((k) => grouped[k].players);
  const prizesData = labels.map((k) => grouped[k].prizes);

  const chartData = {
    labels,
    datasets: [
      {
        label: "Players Registered",
        data: playersData,
        borderColor: "#3b82f6", // Tailwind text-blue-500
        backgroundColor: "rgba(59,130,246,0.1)",
        tension: 0.3,
      },
      {
        label: "Prizes Won",
        data: prizesData,
        borderColor: "#22c55e", // Tailwind text-green-500
        backgroundColor: "rgba(34,197,94,0.1)",
        tension: 0.3,
        yAxisID: "y1",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    layout: {
      padding: 0, // Remove outside padding to match TopPlayers
    },
    plugins: {
      legend: { display: false }, // Hide legend for minimal look
      title: {
        display: true,
        text: "Games & Prizes Over Time",
        color: "#1a2233",
        font: { weight: "bold" as const, size: 13 },
        padding: { top: 20, bottom: 20 },
      },
      tooltip: {
        callbacks: {
          title: function (context: any) {
            return `Month: ${labels[context[0].dataIndex]}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Month",
          color: "#1a2233",
          font: { weight: "bold" as const, size: 13 },
          padding: { top: 16, bottom: 8 },
        },
        ticks: {
          color: "#1a2233",
          font: { size: 12, weight: "bold" as const },
          padding: 10,
        },
        grid: { color: "#eee" },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Players Registered",
          color: "#222",
          font: { weight: "bold" as const, size: 13 },
          padding: { top: 8, bottom: 8 },
        },
        ticks: {
          color: "#bbb",
          font: { weight: "bold" as const, size: 12 },
          padding: 10,
        },
        grid: { color: "#eee" },
      },
      y1: {
        beginAtZero: true,
        position: "right" as const,
        title: {
          display: true,
          text: "Prizes Won",
          color: "#222",
          font: { weight: "bold" as const, size: 13 },
          padding: { top: 8, bottom: 8 },
        },
        ticks: {
          color: "#bbb",
          font: { weight: "bold" as const, size: 12 },
          padding: 10,
        },
        grid: { drawOnChartArea: false, color: "#eee" },
      },
    },
  };

  return (
    <Card className="hover:shadow-lg transition-shadow duration-200 overflow-hidden p-6 border border-gray-800">
      <h2 className="text-lg font-semibold font-heading mb-4 flex items-center text-superone-charcoal">
        <Trophy className="mr-2 h-5 w-5 text-green-500" />
        Games & Prizes
      </h2>
      {loading ? (
        <div className="text-gray-400">Loading...</div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : (
        <div>
          <Line data={chartData} options={chartOptions} height={100} />
        </div>
      )}
    </Card>
  );
}
